import { Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../../components/utils';
import { formatHubEventInlineTime } from '../hubEventFormatting';
import { getLocale } from '../../../../../utils/i18n';

type HubEventItemProps = {
  title: string;
  startDateTime: string;
  timezone: string;
  isAllDay: boolean;
  onClick?: () => void;
};

export function HubEventItem({
  title,
  startDateTime,
  timezone,
  isAllDay,
  onClick,
}: HubEventItemProps) {
  const { t } = useTranslation();
  const {
    palette: { greyscaleLight },
  } = useTheme();

  const time = formatHubEventInlineTime({
    startDateTime,
    timezone,
    isAllDay,
    locale: getLocale(),
    allDayLabel: t('hubAllDay'),
  });

  const content = (
    <>
      <span
        aria-hidden="true"
        className="inline-block rounded-full shrink-0"
        style={{
          width: 8,
          height: 8,
          backgroundColor: greyscaleLight[500],
        }}
      />
      <Typography
        component="span"
        variant="caption"
        className="shrink-0 font-medium leading-4"
        color="text.primary"
      >
        {time}
      </Typography>
      <Typography
        component="span"
        variant="caption"
        className="truncate leading-4"
        color="text.secondary"
      >
        {title}
      </Typography>
    </>
  );

  const itemClassName = cn(
    'flex items-center gap-1 px-2 py-1 h-6 w-full overflow-hidden text-left',
    'bg-transparent border-0 appearance-none',
    onClick && 'cursor-pointer',
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={itemClassName}
        title={`${time} ${title}`}
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={itemClassName} title={`${time} ${title}`}>
      {content}
    </div>
  );
}
